import type { CentreStats, CentreStudent } from '@ielts-buddy/shared'
import { useCallback, useEffect, useState } from 'react'
import { fetchCentreMe, fetchCentreStudents } from '../shared/api.js'
import { useAuthStore } from '../shared/auth-store.js'
import { useRouter } from '../shared/router-store.js'
import { AppNav } from '../shared/ui/AppNav.js'
import { Card } from '../shared/ui/Card.js'
import { ErrorState } from '../shared/ui/ErrorState.js'
import { HeroCard } from '../shared/ui/HeroCard.js'
import { LoadingDots } from '../shared/ui/LoadingDots.js'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatBand(band: number | null) {
  return band != null ? band.toFixed(1) : '—'
}

export function OverviewScreen() {
  const { centre } = useAuthStore()
  const { navigateToStudent, navigateToCentresManagement } = useRouter()

  const [stats, setStats] = useState<CentreStats | null>(null)
  const [students, setStudents] = useState<CentreStudent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [meRes, studentsRes] = await Promise.all([
        fetchCentreMe(),
        fetchCentreStudents(),
      ])
      setStats(meRes.stats)
      setStudents(studentsRes.students)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (!centre) return null

  return (
    <div className="min-h-screen flex flex-col">
      <AppNav />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 animate-fade-in">
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingDots label="Loading your dashboard…" />
          </div>
        ) : error ? (
          <ErrorState
            title="Failed to load data"
            message={error}
            primaryAction={{ label: 'Retry', onClick: load }}
          />
        ) : (
          <div className="flex flex-col gap-5">
            <HeroCard
              label="Your Centre"
              title={centre.name}
              photoUrl={centre.ownerPhotoUrl}
              metrics={[
                { value: stats?.studentCount ?? 0, label: 'Students' },
                {
                  value: formatBand(stats?.avgBand ?? null),
                  label: 'Avg Band',
                },
                {
                  value: `$${stats?.estimatedEarnings.toFixed(0) ?? 0}`,
                  label: 'Est. / month',
                },
              ]}
            />

            {centre.isAdmin && (
              <Card tone="surface" className="p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-brand-text">
                      Centres Management
                    </p>
                    <p className="text-xs text-brand-text-muted">
                      Review registrations now and analytics later
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={navigateToCentresManagement}
                    className="px-4 py-2 rounded-xl bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-hover transition-colors"
                  >
                    Open
                  </button>
                </div>
              </Card>
            )}

            {/* Invite link — primary-soft so it has visual weight without competing with hero */}
            <Card tone="primary-soft" className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-brand-primary-soft border border-brand-border flex items-center justify-center shrink-0">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-brand-primary-strong"
                  >
                    <title>Invite link</title>
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-brand-text">
                    Invite Students
                  </p>
                  <p className="text-xs text-brand-text-muted">
                    Share this link — new students get linked to your centre
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={
                    import.meta.env.VITE_TELEGRAM_BOT_USERNAME
                      ? `https://t.me/${import.meta.env.VITE_TELEGRAM_BOT_USERNAME}?startapp=${centre.slug}`
                      : `https://t.me/?startapp=${centre.slug}`
                  }
                  className="flex-1 min-w-0 bg-brand-surface border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-text-muted font-mono truncate"
                />
                <InviteCopyButton slug={centre.slug} />
              </div>
            </Card>

            {/* Student table */}
            <Card tone="surface" className="overflow-hidden">
              <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
                <h2 className="font-semibold text-brand-text text-sm">
                  Students
                </h2>
                {students.length > 0 && (
                  <span className="text-xs text-brand-text-subtle bg-brand-bg-soft border border-brand-border rounded-full px-2 py-0.5">
                    {students.length}
                  </span>
                )}
              </div>
              {students.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-14 text-center">
                  <div className="w-10 h-10 rounded-full bg-brand-bg-soft border border-brand-border flex items-center justify-center text-brand-text-subtle text-lg">
                    👤
                  </div>
                  <p className="text-brand-text-muted text-sm font-medium">
                    No students yet
                  </p>
                  <p className="text-brand-text-subtle text-xs">
                    Share your invite link above to get started
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-brand-border bg-brand-bg-soft">
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Name
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Sessions
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted">
                          Avg Band
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted hidden md:table-cell">
                          Last Active
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-medium text-brand-text-muted hidden md:table-cell">
                          Joined
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s, i) => (
                        <tr
                          key={s.userId}
                          onClick={() =>
                            navigateToStudent(s.userId, s.firstName)
                          }
                          className={`cursor-pointer hover:bg-brand-bg-soft transition-colors ${
                            i < students.length - 1
                              ? 'border-b border-brand-border'
                              : ''
                          }`}
                        >
                          <td className="px-5 py-3.5 font-medium text-brand-text">
                            {s.firstName}
                            {s.lastName ? ` ${s.lastName}` : ''}
                            {s.username && (
                              <span className="ml-1.5 text-xs text-brand-text-subtle hidden sm:inline">
                                @{s.username}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-brand-text-muted">
                            {s.sessionCount}
                          </td>
                          <td className="px-5 py-3.5">
                            <span
                              className={`font-semibold ${s.avgBand != null ? 'text-brand-primary-strong' : 'text-brand-text-muted'}`}
                            >
                              {formatBand(s.avgBand)}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-brand-text-muted hidden md:table-cell">
                            {s.lastActive ? formatDate(s.lastActive) : '—'}
                          </td>
                          <td className="px-5 py-3.5 text-brand-text-muted hidden md:table-cell">
                            {formatDate(s.joinedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  )
}

function InviteCopyButton({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false)
  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME
  const link = botUsername
    ? `https://t.me/${botUsername}?startapp=${slug}`
    : `https://t.me/?startapp=${slug}`

  const copy = async () => {
    await navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="shrink-0 px-4 py-2 rounded-xl bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-hover transition-colors"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}
